// CreateTagDialog.js
// Dialog for creating new general transaction tags

import React, { useState, useRef } from 'react';
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import MobileDialog from './MobileDialog';
import { createGeneralTransactionTag } from '../../services/cashflowService';

const CreateTagDialog = ({
  open,
  onClose,
  onSuccess,
  defaultType = 'expense', // 'income', 'expense', or 'both'
}) => {
  // Form state
  const [name, setName] = useState('');
  const [type, setType] = useState(defaultType);

  // UI state
  const [localError, setLocalError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);

  // Reset form state
  const resetForm = () => {
    setName('');
    setType(defaultType);
    setLocalError('');
  };

  // Handle form close
  const handleClose = () => {
    resetForm();
    onClose();
  };

  // Submit tag
  const handleSubmit = async () => {
    if (isSubmittingRef.current) return;

    // Validate
    if (!name.trim()) {
      setLocalError('שגיאה: יש להזין שם לקטגוריה.');
      return;
    }

    isSubmittingRef.current = true;
    setIsSubmitting(true);
    setLocalError('');

    try {
      const payload = {
        name: name.trim(),
        type: type,
      };

      const response = await createGeneralTransactionTag(payload);

      if (onSuccess) {
        onSuccess(response);
      }

      handleClose();
    } catch (err) {
      console.error('שגיאה ביצירת קטגוריה:', err);
      setLocalError(
        err?.response?.data?.error ||
        err.message ||
        'שגיאה בעת יצירת הקטגוריה'
      );
    } finally {
      setIsSubmitting(false);
      isSubmittingRef.current = false;
    }
  };

  return (
    <MobileDialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== 'backdropClick') {
          handleClose();
        }
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ fontSize: '1.4rem' }}>
        יצירת קטגוריה חדשה
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {localError && (
            <Alert severity="error">{localError}</Alert>
          )}

          {/* Name */}
          <TextField
            label="שם הקטגוריה"
            type="text"
            fullWidth
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
          />

          {/* Type */}
          <FormControl fullWidth>
            <InputLabel>סוג</InputLabel>
            <Select
              value={type}
              onChange={(e) => setType(e.target.value)}
              label="סוג"
            >
              <MenuItem value="expense">הוצאות</MenuItem>
              <MenuItem value="income">הכנסות</MenuItem>
              <MenuItem value="both">שניהם</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={handleClose}
          color="inherit"
          size="large"
          sx={{ fontSize: '1rem' }}
        >
          ביטול
        </Button>
        <Button
          variant="contained"
          color="primary"
          size="large"
          sx={{ fontSize: '1rem' }}
          onClick={handleSubmit}
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <CircularProgress size={24} />
          ) : (
            'צור'
          )}
        </Button>
      </DialogActions>
    </MobileDialog>
  );
};

export default CreateTagDialog;
